import React, { useEffect, useMemo, useState } from 'react'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useSegment } from 'src/lib/contexts/SegmentContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { Skeleton } from '@mui/material'
import { createManualSegmentPayload, createSegment, editSegment, getSegmentById, parseManualSegmentConfig } from 'src/lib/utils/segmentsHelper'
import { FileTypeOptions, UploadedFileType } from 'src/lib/types/common/files'
import { useUser } from 'src/lib/contexts/UserContext'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'

import {
  Button,
  IconButton,
  Icon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger
} from '@shopflo/ui'
import FormErrorText from '../common/FormErrorText'
import CSVImportModal from './CSVImportModal'
import GenericSelectionModal, { SelectionOption } from '../common/select-component/GenericSelectionModal'
import { MultiSelectItem } from '../common/select-component/GenericMultiSelectDialog'
import { getStates } from 'src/lib/core/api-methods'
import { IndianStateType } from 'src/lib/types/random'
import { SegmentInclusionListType, SegmentInclusionType } from 'src/lib/types/segment'
import { ErrorType } from 'src/lib/hooks/useCSVValidator'
import StateFilterItem from './StateFilterItem'
import TagInput from './TagInput'
import CSVColumnMapping from './CSVColumnMapping'
import { AddressBook, FileArrowUp, FileCsv, MapPinLine, Phone, UploadSimple, Warning, X } from '@phosphor-icons/react'
import TextFieldError from '../common/form-elements/TextFieldError'
import useGTM from 'src/lib/hooks/useGTM'

interface ManualSegmentCreationFlowProps {
  segmentId?: string | string[]
  isEditMode: boolean
  handleClose?: () => void
  onSuccess?: () => void
}

const ManualSegmentCreationFlow: React.FC<ManualSegmentCreationFlowProps> = ({ segmentId, isEditMode, handleClose, onSuccess }) => {
  const {
    getters: { merchant }
  } = useMerchant()
  const { sendActivityLog } = useActivityLogs()
  const { t } = useLocale()
  const { segmentFormState, setSegmentFormState } = useSegment()

  const INSERT_OPTIONS: SelectionOption[] = [{ key: 'state', value: t('segment_creation.state'), icon: <Icon IconComponent={AddressBook} size='sm' className='text-zinc-500' /> }]

  const { sendGTMTrigger } = useGTM()
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode)
  const [isCSVModalOpen, setIsCSVModalOpen] = useState<boolean>(false)
  const [isInsertDropdownOpen, setIsInsertDropdownOpen] = useState<boolean>(false)
  const { user } = useUser()

  // Validation error states
  const [manualInputError, setManualInputError] = useState<string | null>(null)
  const [stateFilterError, setStateFilterError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<boolean>(false)
  const [nameErrorKey, setNameErrorKey] = useState<number>(0)
  const [noDataError, setNoDataError] = useState<boolean>(false)

  // Indian states from API
  const [indianStates, setIndianStates] = useState<MultiSelectItem[]>([])

  // Get STATE filters from inclusionList
  const stateFilters = useMemo(() => {
    return (segmentFormState?.inclusionList || []).filter(item => item.item === 'STATE')
  }, [segmentFormState?.inclusionList])

  const isSaveEnabled = useMemo(() => {
    const hasName = Boolean(segmentFormState?.name)
    const hasFile = Boolean(segmentFormState?.sourceFile) || Boolean(segmentFormState?.currentFile)
    const hasManualInput = Boolean(segmentFormState?.manualInput.trim())
    const hasInclusionFilters = stateFilters.some(f => f.values && f.values.length > 0)

    if (isEditMode) {
      return hasName && (hasFile || hasManualInput || hasInclusionFilters)
    }
    return hasName && (hasFile || hasManualInput || hasInclusionFilters)
  }, [segmentFormState, isEditMode, stateFilters])

  useEffect(() => {
    if (merchant && Boolean(isEditMode) && typeof segmentId === 'string') {
      createSegmentObject(segmentId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant])

  // Fetch Indian states on mount
  useEffect(() => {
    const fetchIndianStates = async () => {
      try {
        const response: any = await getStates('IN')
        if (response?.data) {
          const states: MultiSelectItem[] = response.data.map((state: IndianStateType) => ({
            key: state.code,
            value: state.name,
            description: undefined // Can add pincode count if available from API
          }))
          setIndianStates(states)
        }
      } catch (error) {
        console.error(t('segment_creation.failed_fetch_states'), error)
      }
    }
    fetchIndianStates()
  }, [])

  const createSegmentObject = async (segmentId?: string) => {
    if (typeof segmentId === 'undefined') return
    const segmentObject: any = await getSegmentObjectData(segmentId)
    setSegmentFormState(parseManualSegmentConfig(segmentObject?.data?.data))
  }

  const getSegmentObjectData = async (segmentId: string) => {
    try {
      setIsLoading(true)
      const response: any = await getSegmentById(merchant?.accountId, segmentId)
      if (response?.error) {
        errorToast(response.error.message ? response.error.message : t('something_went_wrong'))
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
        return
      }
      setIsLoading(false)
      return response
    } catch (e) {
      console.error(e)
      errorToast(t('something_went_wrong'))
    }
  }

  // Validation function that runs on save button click
  const validateBeforeSave = (): boolean => {
    let isValid = true
    setManualInputError(null)
    setStateFilterError(null)
    setNoDataError(false)

    const hasFile = Boolean(segmentFormState?.sourceFile) || Boolean(segmentFormState?.currentFile)
    const hasManualInput = Boolean(segmentFormState?.manualInput?.trim())
    const hasInclusionFilters = stateFilters.some(f => f.values && f.values.length > 0)

    if (!segmentFormState?.name?.trim()) {
      setNameError(true)
      setNameErrorKey(k => k + 1)
      isValid = false
    }

    if (!hasFile && !hasManualInput && !hasInclusionFilters) {
      setNoDataError(true)
      isValid = false
    }

    const manualInput = segmentFormState?.manualInput?.trim() || ''

    // Validate manual input based on segment type
    if (manualInput) {
      const lines = manualInput.split('\n').filter(line => line.trim())

      if (segmentFormState?.segmentType === 'PHONE_NUMBER') {
        // Check phone number format: must be at least 12 characters
        const invalidPhoneNumbers = lines.filter(phone => phone.trim().length < 12)
        if (invalidPhoneNumbers.length > 0) {
          setManualInputError(t('segment_creation.invalid_phone_detected', { numbers: invalidPhoneNumbers.slice(0, 3).join(', ') }))
          isValid = false
        }
      } else if (segmentFormState?.segmentType === 'PINCODE') {
        // Check pincode format: must be numeric
        const invalidPincodes = lines.filter(pincode => isNaN(Number(pincode.trim())))
        if (invalidPincodes.length > 0) {
          setManualInputError(t('segment_creation.invalid_pincode_detected', { pincodes: invalidPincodes.slice(0, 3).join(', ') }))
          isValid = false
        }
      }
    }

    // Validate state filters for PINCODE type
    if (segmentFormState?.segmentType === 'PINCODE' && stateFilters.length > 0) {
      const emptyStateFilters = stateFilters.filter(f => f.values.length === 0)
      if (emptyStateFilters.length > 0) {
        setStateFilterError(t('segment_creation.select_state_error'))
        isValid = false
      }
    }

    return isValid
  }

  const handleSave = async () => {
    if (!validateBeforeSave()) return
    if (typeof merchant?.accountId === 'undefined') return
    if (isEditMode && typeof segmentId !== 'string') return
    const segmentRequestPayload = createManualSegmentPayload(segmentFormState);

    setIsLoading(true)
    try {
      const response: any = isEditMode
        ? await editSegment('STATIC', merchant?.accountId, typeof segmentId === 'string' ? segmentId : '', segmentRequestPayload)
        : await createSegment('STATIC', merchant?.accountId, segmentRequestPayload)
      if (response?.error) {
        errorToast(response.error.message ? response.error.message : t('something_went_wrong'))
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
        return
      }

      if (isEditMode) {
        sendGTMTrigger('segment_edit_completed', 'edit', {
          segmentType: "PHONE_NUMBER",
        })
      } else {
        sendGTMTrigger('segment_creation_completed', 'save', {
          segmentType: "PHONE_NUMBER",
        })
      }

      const activitiLogPayload = {
        entityName: EntityName.SEGMENTS,
        actionName: isEditMode ? t('edited_segment') : t('created_segment'),
        metadata: {
          segment_name: segmentFormState?.name,
          segment_description: segmentFormState?.description,
          segment_source_type: segmentFormState?.sourceType
        }
      }
      sendActivityLog(activitiLogPayload)

      successToast(isEditMode ? t('saved_segment_group') : t('created_segment_group'))
      onSuccess?.()
      handleClose?.()
    } catch (e) {
      console.error(e)
      errorToast(t('something_went_wrong'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileUploaded = (uploadedFile: UploadedFileType, uploadedFileFormData: FormData) => {
    setSegmentFormState({
      ...segmentFormState,
      currentFile: uploadedFile,
      sourceFile: uploadedFileFormData
    })
    if (noDataError) setNoDataError(false)
  }

  const handleRemoveFile = () => {
    setSegmentFormState({
      ...segmentFormState,
      currentFile: undefined,
      sourceFile: undefined
    })
  }

  // Handle Insert option selection - adds a new STATE filter to inclusionList
  const handleInsertSelect = (option: SelectionOption) => {
    if (option.key === 'state') {
      // Check which modes are already used by existing STATE filters
      const existingStateFilters = (segmentFormState?.inclusionList || []).filter(item => item.item === 'STATE')
      const usedModes = existingStateFilters.map(item => item.type)

      // Determine the type for the new filter: use the opposite of what's already used
      let newType: SegmentInclusionType = 'INCLUDE'
      if (usedModes.includes('INCLUDE')) {
        newType = 'EXCLUDE'
      } else if (usedModes.includes('EXCLUDE')) {
        newType = 'INCLUDE'
      }

      const newFilter: SegmentInclusionListType = {
        item: 'STATE',
        type: newType,
        values: []
      }
      setSegmentFormState({
        ...segmentFormState,
        inclusionList: [...(segmentFormState?.inclusionList || []), newFilter]
      })
      setIsInsertDropdownOpen(false)
    }
  }

  // Handle mode change (Include / Exclude)
  const handleModeChange = (index: number, mode: SegmentInclusionType) => {
    const updatedList = [...(segmentFormState?.inclusionList || [])]
    // Find the actual index in inclusionList (not just STATE filters)
    const stateFilterIndices = updatedList
      .map((item, idx) => (item.item === 'STATE' ? idx : -1))
      .filter(idx => idx !== -1)

    const actualIndex = stateFilterIndices[index]
    if (actualIndex !== undefined && actualIndex !== -1) {
      updatedList[actualIndex] = { ...updatedList[actualIndex], type: mode }
      setSegmentFormState({
        ...segmentFormState,
        inclusionList: updatedList
      })
    }
  }

  // Handle state selection change
  const handleStateSelectionChange = (index: number, selectedKeys: string[]) => {
    const updatedList = [...(segmentFormState?.inclusionList || [])]
    // Find the actual index in inclusionList (not just STATE filters)
    const stateFilterIndices = updatedList
      .map((item, idx) => (item.item === 'STATE' ? idx : -1))
      .filter(idx => idx !== -1)

    const actualIndex = stateFilterIndices[index]
    if (actualIndex !== undefined && actualIndex !== -1) {
      updatedList[actualIndex] = { ...updatedList[actualIndex], values: selectedKeys }
      setSegmentFormState({
        ...segmentFormState,
        inclusionList: updatedList
      })
    }
  }

  // Handle remove filter
  const handleRemoveFilter = (index: number) => {
    const updatedList = [...(segmentFormState?.inclusionList || [])]
    // Find the actual index in inclusionList (not just STATE filters)
    const stateFilterIndices = updatedList
      .map((item, idx) => (item.item === 'STATE' ? idx : -1))
      .filter(idx => idx !== -1)

    const actualIndex = stateFilterIndices[index]
    if (actualIndex !== undefined && actualIndex !== -1) {
      updatedList.splice(actualIndex, 1)
      setSegmentFormState({
        ...segmentFormState,
        inclusionList: updatedList
      })
    }
  }

  return (
    <div className='flex flex-col h-full'>
      {isEditMode && isLoading ? (
        <div className='p-8 space-y-4'>
          <Skeleton variant='rectangular' sx={{ borderRadius: '8px' }} width={'100%'} height={100} />
          <Skeleton variant='rectangular' sx={{ borderRadius: '8px' }} width={'100%'} height={60} />
          <Skeleton variant='rectangular' sx={{ borderRadius: '8px' }} width={'100%'} height={120} />
        </div>
      ) : (
        <div className='flex flex-col h-full'>
          {/* Main Content */}
          <div className='flex-1 overflow-y-auto'>
            <div className='flex flex-col gap-5 pt-6'>
              {/* Header Section - Name & Description */}
              <div className='flex flex-col gap-2 px-8'>
                {/* Static Badge */}
                <div className='flex items-center gap-1'>
                  <Icon IconComponent={AddressBook} size='xs' className='text-pink-700' iconProps={{ weight: 'fill' }} />
                  <span className='text-text-sm-medium text-pink-700'>{t('segment_creation.static')}</span>
                </div>

                <div className='flex flex-col gap-1'>
                  {/* Name Input */}
                  <input
                    key={nameErrorKey}
                    type='text'
                    placeholder={t('segment_creation.static_name_placeholder')}
                    value={segmentFormState?.name ?? ''}
                    onChange={e => {
                      if (e.target.value.length > 36) return
                      setSegmentFormState({ ...segmentFormState, name: e.target.value })
                      if (nameError) setNameError(false)
                    }}
                    autoFocus
                    className={`text-text-lg-semibold text-coal-dark bg-transparent border-none outline-none w-full ${nameError ? 'animate-wiggle placeholder:text-red-300' : 'placeholder:text-zinc-400/70'}`}
                  />

                  {/* Description Input */}
                  <input
                    type='text'
                    placeholder={t('segment_creation.description_placeholder')}
                    value={segmentFormState?.description ?? ''}
                    onChange={e => {
                      if (e.target.value.length > 80) return
                      setSegmentFormState({ ...segmentFormState, description: e.target.value })
                    }}
                    className='text-text-sm-regular text-zinc-500 placeholder:text-zinc-400/70 bg-transparent border-none outline-none w-full'
                  />
                  <div className={`grid transition-all duration-200 ease-in-out ${nameError ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className='overflow-hidden'>
                      <FormErrorText message={t('segment_creation.name_required_error')} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Type Section */}
              <div className='flex flex-col gap-2 px-8'>
                <span className='text-text-xs-medium text-gray-500'>{t('segment_creation.type_label')}</span>
                <Tabs
                  value={segmentFormState?.segmentType || 'PINCODE'}
                  onValueChange={key => {
                    if (key === 'PINCODE') {
                      setSegmentFormState({
                        ...segmentFormState,
                        segmentType: 'PINCODE',
                        currentFile: undefined,
                        sourceFile: undefined
                      })
                      return
                    }
                    const filteredList = (segmentFormState?.inclusionList || []).filter(item => item.item !== 'STATE')
                    setSegmentFormState({
                      ...segmentFormState,
                      segmentType: 'PHONE_NUMBER',
                      inclusionList: filteredList,
                      currentFile: undefined,
                      sourceFile: undefined
                    })
                  }}
                >
                  <TabsList variant='neutral' size='md'>
                    <TabsTrigger value='PINCODE' leftIcon={MapPinLine}>
                      {t('segment_creation.pincode')}
                    </TabsTrigger>
                    <TabsTrigger value='PHONE_NUMBER' leftIcon={Phone}>
                      {t('segment_creation.phone_number')}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div> 

              {/* Actions Section */}
              <div className='flex flex-col px-8'>
                <div className='border-t-1 border-zinc-950/5 pt-5'>
                  {/* Action Buttons - Import CSV & Insert */}
                  <div className='flex flex-col gap-1.5 pb-3'>
                    {/* Uploaded Files */}
                    {segmentFormState?.currentFile ? (
                      <div className='flex items-center gap-3 py-2.5 border-b-1 border-zinc-950/5'>
                        <span className='text-text-sm-medium text-zinc-500 w-8 shrink-0'>{t('segment_creation.from')}</span>
                        <div className='flex-1 min-w-0 flex items-center'>
                          <div
                            className='flex min-w-0 max-w-full items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-zinc-600 cursor-pointer transition-colors bg-zinc-950/5 hover:bg-zinc-950/8 overflow-hidden'
                            onClick={() => {
                              window.open(segmentFormState.currentFile?.url, '_blank')
                            }}
                          >
                            <FileCsv size={14} className='text-gray-400 shrink-0' />
                            <span className='truncate min-w-0'>{segmentFormState.currentFile.name}</span>
                          </div>
                        </div>
                        <div className='flex items-center shrink-0'>
                          <IconButton IconComponent={X} size='xs' variant='ghost' onClick={handleRemoveFile} />
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant='dashed'
                        leftIcon={UploadSimple}
                        size='sm'
                        onClick={() => setIsCSVModalOpen(true)}
                        className='bg-transparent h-7 w-fit text-text-sm-medium'
                      >
                        {t('segment_creation.import_csv')}
                      </Button>
                    )}

                    {/* State Filters */}
                    {stateFilters.map((filter, index) => (
                      <StateFilterItem
                        key={`state-filter-${index}`}
                        filter={filter}
                        index={index}
                        isFirst={index === 0}
                        hasFileAbove={Boolean(segmentFormState?.currentFile)}
                        indianStates={indianStates}
                        inclusionList={segmentFormState?.inclusionList || []}
                        hasError={Boolean(stateFilterError) && filter.values.length === 0}
                        onModeChange={(idx, mode) => {
                          handleModeChange(idx, mode)
                          if (stateFilterError) setStateFilterError(null)
                        }}
                        onStateChange={(idx, keys) => {
                          handleStateSelectionChange(idx, keys)
                          if (stateFilterError) setStateFilterError(null)
                        }}
                        onRemove={handleRemoveFilter}
                      />
                    ))}

                    {/* Insert Button - Opens State selection */}
                    {segmentFormState?.segmentType === 'PINCODE' && stateFilters.length < 2 && (
                      <DropdownMenu open={isInsertDropdownOpen} onOpenChange={setIsInsertDropdownOpen}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='dashed'
                            leftIcon={FileArrowUp}
                            size='sm'
                            className='bg-transparent h-7 w-fit text-text-sm-medium'
                          >
                            {t('segment_creation.insert')}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align='start'
                          className='w-[220px] p-1.5 shadow-[0_0_0_0.5px_rgba(9,9,11,0.12),0_16px_16px_-6px_rgba(9,9,11,0.12),0_2px_10px_-6px_rgba(9,9,11,0.48)] rounded-xl'
                        >
                          {INSERT_OPTIONS.map(option => (
                            <div
                              key={option.key}
                              className='flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-100 cursor-pointer'
                              onClick={() => handleInsertSelect(option)}
                            >
                              {option.icon}
                              <span className='text-text-sm-regular text-zinc-600'>{option.value}</span>
                            </div>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>

              {/* Manual Entry - Pill Input */}
              <div className='px-5 mb-2'>
                <TagInput
                  value={segmentFormState?.manualInput ?? ''}
                  variant={segmentFormState?.segmentType === 'PINCODE' || segmentFormState?.segmentType === 'PHONE_NUMBER' ? 'number' : 'string'}
                  onChange={manualInput => {
                    setSegmentFormState({ ...segmentFormState, manualInput })
                    if (manualInputError) setManualInputError(null)
                    if (noDataError) setNoDataError(false)
                  }}
                  placeholder={
                    segmentFormState?.segmentType === 'PINCODE'
                      ? t('segment_creation.pincode_input_placeholder')
                      : t('segment_creation.phone_input_placeholder')
                  }
                  error={Boolean(manualInputError)}
                  className='h-36'
                />
                {manualInputError && (
                  <div className='flex items-center gap-1.5 mt-1.5 text-red-dark'>
                    <Warning size={14} />
                    <TextFieldError message={manualInputError} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className='flex flex-col gap-1.5 px-5 pt-3 pb-4'>
            <Button
              id='segment-save-button'
              variant='primary'
              size='md'
              onClick={handleSave}
              loading={isLoading}
              disabled={isLoading || user?.isDemoUser}
              className='w-full'
            >
              {t('segment_creation.save_segment')}
            </Button>
            <Button
              variant='ghost'
              size='md'
              onClick={handleClose}
              disabled={isLoading}
              className='w-full'
            >
              {t('segment_creation.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={isCSVModalOpen}
        handleClose={() => setIsCSVModalOpen(false)}
        onFileUploaded={handleFileUploaded}
        type={FileTypeOptions.CSV}
        acceptedSize={10 * 1024 * 1024}
        errorsToCheck={[
          ErrorType.FILE_SIZE_EXCEEDED,
          ...(segmentFormState?.segmentType === 'PINCODE'
            ? [ErrorType.SEGMENT_PINCODE_LIST]
            : [ErrorType.SEGMENT_PHONE_NUMBER])
        ]}
        sampleLink={
          segmentFormState?.segmentType === 'PINCODE'
            ? 'https://documents.shopflo.com/sample_pincodes.csv'
            : 'https://documents.shopflo.com/sample_numbers.csv'
        }
        successComponent={<CSVColumnMapping segmentType={segmentFormState?.segmentType || 'PINCODE'} />}
      />
    </div>
  )
}

export default ManualSegmentCreationFlow
